import { forwardRef, type HTMLAttributes } from 'react';
import { useMediaQuery } from 'react-responsive';
import { DialogVisuallyHidden, Drawer, DrawerContent, DrawerOverlay, DrawerTitle } from '@zep-us/zus';
import { motion } from 'framer-motion';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { useAiBuilderMobileDrawerViewport } from '../hooks/useAiBuilderMobileDrawerViewport';
import { AiBuilderAttachmentPreparationProvider } from './AiBuilderAttachmentPreparationProvider';
import { AiBuilderChatPanelContent } from './AiBuilderChatPanelContent';
import { AI_BUILDER_CHAT_DRAWER_BREAKPOINT, type AiBuilderShellTransitionState, getAiBuilderChatPanelVariants } from './AiBuilderShellTransition';
const MOBILE_DRAWER_CONTENT_CLASS_NAME = 'inset-x-0 bottom-0 top-[95px] mt-0 !z-[60] h-auto rounded-t-md border-none bg-default ![animation-duration:360ms] data-[state=closed]:![animation-duration:240ms]';
const AiBuilderDrawerDim = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((props, ref) => <DrawerOverlay ref={ref} aria-hidden="true" data-ai-builder-chat-backdrop {...props} className="!z-[59] bg-dimmed" />);
AiBuilderDrawerDim.displayName = 'AiBuilderDrawerDim';
export const AiBuilderChatPanel = ({
  transitionState
}: {
  transitionState: AiBuilderShellTransitionState;
}) => <AiBuilderAttachmentPreparationProvider data-sentry-element="AiBuilderAttachmentPreparationProvider" data-sentry-component="AiBuilderChatPanel" data-sentry-source-file="AiBuilderChatPanel.tsx">
    <AiBuilderChatPanelResponsiveShell transitionState={transitionState} data-sentry-element="AiBuilderChatPanelResponsiveShell" data-sentry-source-file="AiBuilderChatPanel.tsx" />
  </AiBuilderAttachmentPreparationProvider>;
const AiBuilderChatPanelResponsiveShell = ({
  transitionState
}: {
  transitionState: AiBuilderShellTransitionState;
}) => {
  const copy = useAiBuilderChatCopy();
  const usesDrawerLayout = useMediaQuery({
    maxWidth: AI_BUILDER_CHAT_DRAWER_BREAKPOINT
  });
  const {
    drawerRef: mobileDrawerContentRef,
    setDrawerRef: setMobileDrawerContentRef,
    setQuestionComposerOverlayRef
  } = useAiBuilderMobileDrawerViewport(usesDrawerLayout);
  if (usesDrawerLayout) {
    return <Drawer open={transitionState !== 'closing'} dismissible={false} modal={true} repositionInputs={false} shouldScaleBackground={false}>
        <DrawerContent ref={setMobileDrawerContentRef} aria-describedby={undefined} className={MOBILE_DRAWER_CONTENT_CLASS_NAME} CustomDim={AiBuilderDrawerDim} onDimClick={() => {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement && mobileDrawerContentRef.current?.contains(activeElement)) {
          activeElement.blur();
        }
      }}>
          <DialogVisuallyHidden>
            <DrawerTitle>{copy.panel.title}</DrawerTitle>
          </DialogVisuallyHidden>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-md" data-ai-builder-chat-surface>
            <AiBuilderChatPanelContent questionComposerOverlayRef={setQuestionComposerOverlayRef} shouldAutoFocusComposer={transitionState === 'open'} />
          </div>
        </DrawerContent>
      </Drawer>;
  }
  return <motion.aside className="fixed bottom-0 right-0 top-0 z-[60] flex min-h-0 w-full flex-none flex-col bg-default tablet-sm:w-1/2 laptop-sm:w-[550px]" initial="closed" animate={transitionState} variants={getAiBuilderChatPanelVariants()} data-sentry-element="unknown" data-sentry-component="AiBuilderChatPanelResponsiveShell" data-sentry-source-file="AiBuilderChatPanel.tsx">
      <AiBuilderChatPanelContent data-sentry-element="AiBuilderChatPanelContent" data-sentry-source-file="AiBuilderChatPanel.tsx" />
    </motion.aside>;
};